import { useEffect, useRef, useState } from "react";

/**
 * Google Places Autocomplete input.
 * Falls back to a plain text input if REACT_APP_GOOGLE_PLACES_API_KEY is not set.
 */
export default function GooglePlacesInput({ value, onChange, placeholder = "Property address", testId = "address-input" }) {
  const inputRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const key = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

  useEffect(() => {
    if (!key) return;
    if (window.google && window.google.maps && window.google.maps.places) {
      setLoaded(true);
      return;
    }
    const scriptId = "google-maps-places";
    if (document.getElementById(scriptId)) {
      const check = setInterval(() => {
        if (window.google?.maps?.places) {
          setLoaded(true);
          clearInterval(check);
        }
      }, 200);
      return () => clearInterval(check);
    }
    const s = document.createElement("script");
    s.id = scriptId;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => setLoaded(true);
    document.body.appendChild(s);
  }, [key]);

  useEffect(() => {
    // Only run once when Google Places script becomes available. onChange is
    // intentionally omitted from deps: re-binding autocomplete on every parent
    // render would attach duplicate listeners.
    if (!loaded || !inputRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: ["us"] },
      fields: ["formatted_address", "address_components"],
    });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const parts = { city: "", state: "", zip: "" };
      (place.address_components || []).forEach((c) => {
        if (c.types.includes("locality")) parts.city = c.long_name;
        else if (c.types.includes("administrative_area_level_1")) parts.state = c.short_name;
        else if (c.types.includes("postal_code")) parts.zip = c.long_name;
      });
      onChange({ address: place.formatted_address || inputRef.current.value, ...parts });
    });
    return () => {
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [loaded]);

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="text"
      autoComplete="street-address"
      className="w-full h-14 px-4 rounded-md border-2 border-[var(--border-soft)] focus:border-[var(--green-500)] focus:outline-none text-lg bg-white"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange({ address: e.target.value })}
      data-testid={testId}
    />
  );
}
